"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Employer pilot request form (owner feedback 2026-08-26 f: the "Talk to us
 * about a team pilot" CTA previously only pre-selected a topic further down
 * the page — it "did nothing" visibly). Now a dedicated dialog collecting
 * the enterprise basics and handing off via mailto: to hello@bitebalance.de
 * — the same honest no-backend handoff the contact section uses (the mail
 * is composed in the visitor's own client; we never claim it was "sent").
 */

import { useState } from "react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useI18n } from "@/i18n"

const CONTACT_EMAIL = "hello@bitebalance.de"

interface EmployerPilotDialogProps {
  open: boolean
  onClose: () => void
}

export function EmployerPilotDialog({ open, onClose }: EmployerPilotDialogProps) {
  const { t } = useI18n()
  const [company, setCompany] = useState("")
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [teamSize, setTeamSize] = useState("")
  const [message, setMessage] = useState("")

  if (!open) return null

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const subject = encodeURIComponent(
      `${t("landingPage.employerDialog.mailSubject")} — ${company}`
    )
    const body = encodeURIComponent(
      [
        `${t("landingPage.employerDialog.companyLabel")}: ${company}`,
        `${t("landingPage.employerDialog.nameLabel")}: ${name}`,
        `${t("landingPage.employerDialog.emailLabel")}: ${email}`,
        `${t("landingPage.employerDialog.teamSizeLabel")}: ${teamSize}`,
        "",
        message,
      ].join("\n")
    )
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`
    onClose()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="employer-pilot-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-bb-xl border border-line bg-paper p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="employer-pilot-title" className="font-display text-xl font-semibold text-ink">
              {t("landingPage.employerDialog.title")}
            </h2>
            <p className="mt-1 text-sm text-ink-2">{t("landingPage.employerDialog.lead")}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="rounded-bb-full p-2 text-muted-foreground transition-colors hover:bg-cream hover:text-ink"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        </div>

        <form className="mt-5 flex flex-col gap-3" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-1.5 text-sm font-medium text-ink">
            {t("landingPage.employerDialog.companyLabel")}
            <Input
              required
              value={company}
              onChange={e => setCompany(e.target.value)}
              autoComplete="organization"
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm font-medium text-ink">
              {t("landingPage.employerDialog.nameLabel")}
              <Input
                required
                value={name}
                onChange={e => setName(e.target.value)}
                autoComplete="name"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-medium text-ink">
              {t("landingPage.employerDialog.teamSizeLabel")}
              <Input
                required
                inputMode="numeric"
                value={teamSize}
                onChange={e => setTeamSize(e.target.value)}
                placeholder={t("landingPage.employerDialog.teamSizePlaceholder")}
              />
            </label>
          </div>
          <label className="flex flex-col gap-1.5 text-sm font-medium text-ink">
            {t("landingPage.employerDialog.emailLabel")}
            <Input
              required
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium text-ink">
            {t("landingPage.employerDialog.messageLabel")}
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={3}
              className="rounded-bb-md border border-line bg-paper px-3 py-2 text-sm text-ink outline-none focus-visible:border-green focus-visible:ring-2 focus-visible:ring-green/20"
              placeholder={t("landingPage.employerDialog.messagePlaceholder")}
            />
          </label>
          <p className="text-xs text-muted-foreground">
            {t("landingPage.employerDialog.mailtoNote")}
          </p>
          <Button type="submit" className="min-h-11 rounded-bb-full font-bold">
            {t("landingPage.employerDialog.submit")}
          </Button>
        </form>
      </div>
    </div>
  )
}
